import React, { useMemo } from "react";
import { ArrowDown, ArrowUp, MoreHorizontal } from "lucide-react";
import { FazeDot, FazePill, etapa, nazevEtapy } from "../lib/faze";
import { fmtKcKratce, relDay } from "../lib/format";
import { sklon } from "../lib/filtr";
import { Kbd } from "../lib/zkratky";
import { KrokCip, Lide, tabuleEtapy, uzavreny } from "./ObchodTabule";
import TechIkony from "./TechIkony";

/**
 * Seznam obchodních případů (Linear): skupiny s přilepenou hlavičkou (počet,
 * součet), na počítači sloupce podle Vlastností, na telefonu karty.
 */
function skupiny(polozky, seskupit, tymMapa, me) {
  if (seskupit === "nic") return [{ klic: "vse", nazev: null, polozky }];
  const m = new Map();
  const pridej = (klic, nazev, p, poradi, faze = null) => {
    if (!m.has(klic)) m.set(klic, { klic, nazev, polozky: [], poradi, faze });
    m.get(klic).polozky.push(p);
  };
  for (const p of polozky) {
    if (seskupit === "faze") {
      const e = etapa(p);
      const poradi = [...tabuleEtapy(), "ZTRACENO"].indexOf(e);
      pridej(e, nazevEtapy(e), p, poradi < 0 ? 99 : poradi, e);
    } else if (seskupit === "obchodnik") {
      const id = p.obchodnik_id;
      const jm = id === me ? "Já" : tymMapa[id]?.name || (id ? "Bývalý kolega" : "Bez obchodníka");
      pridej(id || "_nikdo", jm, p, id === me ? -1 : id ? 0 : 1);
    } else {
      // podle hlavní (první živé) technologie případu
      const t = (p.technologie || []).find((x) => x.faze !== "ZTRACENO") || (p.technologie || [])[0];
      pridej(t?.kategorie_name || "_bez", t?.kategorie_name || "Bez technologie", p, t ? 0 : 1);
    }
  }
  return [...m.values()].sort((a, b) => a.poradi - b.poradi || String(a.nazev).localeCompare(String(b.nazev), "cs"));
}

export default function ObchodSeznam({ polozky, z, setRazeni, money, tymMapa, me, onDetail, onMenu, onNahled, onKrok, mobil }) {
  const vl = z.vlastnosti;
  const grupy = useMemo(() => skupiny(polozky, z.seskupit, tymMapa, me), [polozky, z.seskupit, tymMapa, me]);
  const suma = (xs) => xs.filter((p) => p.faze !== "ZTRACENO").reduce((a, p) => a + (p.hodnota || 0), 0);
  const sloupce = [
    "minmax(0,1fr)",
    vl.technologie && "140px", vl.krok && "200px", vl.obchodnik && "32px", vl.dni && "70px",
    money && vl.hodnota && "110px", "90px",
  ].filter(Boolean).join(" ");
  const Zahlavi = ({ klic, children, vpravo }) => {
    const akt = z.razeni === klic;
    return (
      <span role="columnheader" aria-sort={akt ? (z.smer === "asc" ? "ascending" : "descending") : "none"}
        className={`flex ${vpravo ? "justify-end" : ""}`}>
        <button type="button" onClick={() => setRazeni(klic)} data-testid={`seznam-razeni-${klic}`}
          className={`label-up flex items-center gap-1 hover:text-ink ${akt ? "text-ink" : ""}`}>
          {children}{akt && (z.smer === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
        </button>
      </span>
    );
  };

  return (
    <div className="card overflow-hidden" data-testid="obchod-seznam">
      {!mobil && (
        <div className="grid gap-3 px-4 py-2.5 label-up text-muted border-b border-line" style={{ gridTemplateColumns: sloupce }}>
          <Zahlavi klic="nazev">Případ</Zahlavi>
          {vl.technologie && <span>Technologie</span>}
          {vl.krok && <Zahlavi klic="krok">Další krok</Zahlavi>}
          {vl.obchodnik && <span title="Obchodník" />}
          {vl.dni && <Zahlavi klic="dni" vpravo>Ve fázi</Zahlavi>}
          {money && vl.hodnota && <Zahlavi klic="hodnota" vpravo>Hodnota</Zahlavi>}
          <Zahlavi klic="zmena">Změna</Zahlavi>
        </div>
      )}
      {grupy.map((g) => (
        <section key={g.klic} aria-label={g.nazev || "Případy"}>
          {g.nazev && (
            <div className="sticky top-0 z-[1] mx-2 mt-2 h-[34px] rounded-[10px] bg-inputbg px-3 flex items-center gap-2 t-meta" data-testid={`skupina-${g.klic}`}>
              {g.faze && <FazeDot faze={g.faze} />}
              <span className="font-700 text-ink">{g.nazev}</span>
              <span className="text-sec amount">{g.polozky.length}</span>
              {money && <span className="ml-auto text-sec amount">{fmtKcKratce(suma(g.polozky))}</span>}
            </div>
          )}
          {g.polozky.map((p) => (
            <div key={p.id} role="link" tabIndex={0} data-testid={`radek-${p.id}`} onClick={() => onDetail(p)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter") onDetail(p);
                if (e.key === " ") { e.preventDefault(); onNahled(p); }
              }}
              className="cursor-pointer px-4 py-3 border-b border-line-softer last:border-0 hover:bg-inputbg focus-visible:outline-none focus-visible:bg-accent/[.06]">
              {mobil ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-2">
                    <span className="flex-1 min-w-0 text-[15px] font-700 text-ink line-clamp-2">{p.nazev}</span>
                    {z.seskupit !== "faze" && <FazePill faze={etapa(p)} />}
                    <button type="button" aria-label={`Další akce: ${p.nazev}`} onClick={(e) => { e.stopPropagation(); onMenu(p, e.currentTarget); }}
                      className="tap -mr-2 -mt-2 shrink-0 inline-flex items-center justify-center text-muted"><MoreHorizontal size={18} /></button>
                  </div>
                  <span className="t-meta text-sec truncate">
                    {[vl.klient && (p.client_name || "klient chybí"), money && vl.hodnota && fmtKcKratce(p.hodnota || 0),
                      vl.technologie && (p.technologie || []).map((t) => t.kategorie_name).join(", "),
                      vl.dni && !uzavreny(p) && `${p.dni_ve_fazi} ${sklon(p.dni_ve_fazi, ["den", "dny", "dní"])} ve fázi`]
                      .filter(Boolean).join(" · ")}
                  </span>
                  {vl.krok && <KrokCip p={p} maly onOtevri={onKrok} />}
                </div>
              ) : (
                <div className="grid gap-3 items-center" style={{ gridTemplateColumns: sloupce }}>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 min-w-0">
                      {z.seskupit !== "faze" && <FazeDot faze={etapa(p)} />}
                      <span className="t-telo font-700 text-ink truncate">{p.nazev}</span>
                    </span>
                    {vl.klient && <span className="block t-meta text-muted truncate">{p.client_name || "klient chybí"} · {p.cislo}</span>}
                  </span>
                  {vl.technologie && <span className="min-w-0"><TechIkony technologie={p.technologie || []} /></span>}
                  {vl.krok && <span className="min-w-0 flex"><KrokCip p={p} maly onOtevri={onKrok} /></span>}
                  {vl.obchodnik && <span className="flex"><Lide p={{ obchodnik_id: p.obchodnik_id }} tymMapa={tymMapa} size={24} /></span>}
                  {vl.dni && <span className="amount t-meta text-sec text-right">{uzavreny(p) ? "—" : `${p.dni_ve_fazi} d`}</span>}
                  {money && vl.hodnota && <span className="amount t-telo font-700 text-ink text-right">{fmtKcKratce(p.hodnota || 0)}</span>}
                  <span className="t-meta text-muted">{relDay(p.updated_at)}</span>
                </div>
              )}
            </div>
          ))}
        </section>
      ))}
      {!mobil && (
        <div className="h-9 px-4 border-t border-line flex items-center gap-4 t-meta text-muted">
          <span><b className="amount text-ink">{polozky.length}</b> {sklon(polozky.length, ["případ", "případy", "případů"])}
            {money && <> · <b className="amount text-ink">{fmtKcKratce(suma(polozky))}</b></>}</span>
          <span className="ml-auto hidden md:flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Kbd>F</Kbd> filtr</span>
            <span className="inline-flex items-center gap-1"><Kbd>/</Kbd> hledat</span>
            <span className="inline-flex items-center gap-1"><Kbd>V</Kbd> zobrazení</span>
            <span className="inline-flex items-center gap-1"><Kbd>␣</Kbd> náhled</span>
            <span className="inline-flex items-center gap-1"><Kbd>?</Kbd> zkratky</span>
          </span>
        </div>
      )}
    </div>
  );
}
